import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Siren, Phone, Copy, PlayCircle, CheckCircle, ArrowBendDownRight, Warning } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { useAccessLog } from "@/pages/AdminAccessLog";

const SAMPLES = [
  "Hi this is Maria in 2B at 754 South 4th Street, water is pouring through my ceiling from upstairs, it's everywhere, please send someone now. My number is 267-555-0142.",
  "This is Dave at 1449 North Corlies, I'm locked out, lost my keys, it's 11pm and I have my kid with me. 215-555-0199.",
  "Hey it's unit 3 at 5346 Chew Ave, my kitchen faucet drips a little, not urgent, just wanted to let you know.",
  "I smell gas in the hallway at 2028 North 5th Street, pretty strong.",
];

const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

function Checklist({ data }) {
  if (!data) return null;
  return (
    <div className="border-2 border-slate-900 bg-white p-4 space-y-3" data-testid="phone-checklist">
      <div className="flex items-center gap-2 font-black uppercase tracking-widest text-xs"><Phone weight="fill"/> Connect your phone number · Twilio toll-free registration</div>
      <p className="text-xs text-slate-600">Every company registers its <b>own</b> number under its own legal name — Twilio approves these in 1–3 business days. Copy each value into Twilio's form.</p>
      <div className="divide-y divide-slate-200">
        {data.fields.map(f => (
          <div key={f.label} className="py-2 flex items-start gap-2">
            <div className="w-40 shrink-0 text-[10px] font-black uppercase tracking-wider text-slate-500 pt-1">{f.label}</div>
            <div className="flex-1 text-xs text-slate-800 break-words">{f.value}{f.hint && <div className="text-[10px] text-slate-400">{f.hint}</div>}</div>
            <button onClick={() => copy(f.value)} className="p-1 text-slate-500 hover:text-slate-900" data-testid={`copy-${f.label.replace(/\W+/g, "-").toLowerCase()}`}><Copy size={14}/></button>
          </div>
        ))}
      </div>
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 pt-2">Webhooks to paste on the number</div>
      {data.webhooks.map(w => (
        <div key={w.label} className="flex items-center gap-2 text-xs"><span className="w-40 shrink-0 text-slate-500">{w.label}</span><code className="flex-1 bg-slate-100 px-2 py-1 text-[11px] break-all">{w.value}</code><button onClick={() => copy(w.value)}><Copy size={14}/></button></div>
      ))}
    </div>
  );
}

function Incident({ inc, onChange }) {
  const [tech, setTech] = useState("");
  const ringing = (inc.chain || []).find(l => l.status === "ringing");
  const act = async (path, body) => {
    try { await api.post(`/dispatch/incidents/${inc.id}/${path}`, body || {}); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };
  const tone = inc.status === "accepted" ? "border-emerald-500" : inc.status === "unanswered" ? "border-red-600" : inc.is_emergency ? "border-amber-500" : "border-slate-300";
  return (
    <div className={`border-2 ${tone} bg-white p-3 space-y-2`} data-testid={`incident-${inc.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 text-white ${inc.is_emergency ? "bg-red-600" : "bg-slate-500"}`}>{inc.emergency_category.replace(/_/g, " ")}</span>
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{inc.status.replace(/_/g, " ")}</span>
        <span className="text-[10px] text-slate-500 ml-auto">{new Date(inc.created_at).toLocaleString()}</span>
      </div>
      <div className="text-sm font-bold text-slate-900">{inc.triage?.summary || inc.transcript.slice(0, 120)}</div>
      <div className="text-xs text-slate-600">{inc.triage?.property_address}{inc.triage?.unit ? ` · #${inc.triage.unit}` : ""} · caller {inc.triage?.caller_name || "?"} {inc.triage?.callback_number || inc.from_number}</div>
      <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 px-2 py-1"><b>Playbook:</b> {inc.playbook}</div>
      {(inc.chain || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {inc.chain.map(l => (
            <span key={l.level} className={`text-[10px] px-2 py-1 border font-bold ${l.status === "accepted" ? "bg-emerald-100 border-emerald-500 text-emerald-900" : l.status === "ringing" ? "bg-amber-100 border-amber-500 text-amber-900 animate-pulse" : l.status === "no_answer" ? "bg-red-50 border-red-300 text-red-800 line-through" : "bg-slate-50 border-slate-300 text-slate-500"}`}>
              L{l.level} · {l.techs.map(t => t.tech_name).join(", ")} {l.status === "accepted" ? "✓" : ""}
            </span>
          ))}
        </div>
      )}
      {ringing && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <input value={tech} onChange={e => setTech(e.target.value)} placeholder={ringing.techs[0]?.tech_name || "tech name"} className="h-8 px-2 border border-slate-300 text-xs w-44" data-testid={`accept-name-${inc.id}`}/>
          <button onClick={() => act("accept", { level: ringing.level, tech_name: tech || ringing.techs[0]?.tech_name || "tech", eta_minutes: 30 })} className="h-8 px-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest" data-testid={`accept-${inc.id}`}><CheckCircle weight="fill" size={12} className="inline mr-1"/> Simulate "press 1" (L{ringing.level})</button>
          <button onClick={() => act("escalate")} className="h-8 px-3 border-2 border-red-500 text-red-700 text-[10px] font-black uppercase tracking-widest" data-testid={`escalate-${inc.id}`}><ArrowBendDownRight weight="bold" size={12} className="inline mr-1"/> No answer → escalate</button>
        </div>
      )}
      <details className="text-[11px]"><summary className="cursor-pointer text-slate-500 font-bold">Timeline ({inc.timeline.length})</summary>
        <ul className="mt-1 space-y-0.5 font-mono text-[10px] text-slate-700">{inc.timeline.map((t, i) => <li key={i}>{t.at.slice(11, 19)} · <b>{t.event}</b> — {t.detail}</li>)}</ul>
      </details>
    </div>
  );
}

export default function AdminEmergencyDispatch() {
  const [settings, setSettings] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [transcript, setTranscript] = useState(SAMPLES[0]);
  const [busy, setBusy] = useState(false);
  useAccessLog("emergency_dispatch");
  const load = () => {
    api.get("/dispatch/settings").then(r => setSettings(r.data));
    api.get("/dispatch/phone-checklist").then(r => setChecklist(r.data));
    api.get("/dispatch/incidents").then(r => setIncidents(r.data.incidents));
  };
  useEffect(load, []);
  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    await api.post("/dispatch/settings", next);
    toast.success(patch.enabled === undefined ? "Saved" : patch.enabled ? "Emergency line ON" : "Emergency line OFF");
  };
  const simulate = async () => {
    setBusy(true);
    try { await api.post("/dispatch/simulate", { transcript, from_number: "+12675550142" }); toast.success("Call triaged"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Simulate failed")); }
    finally { setBusy(false); }
  };
  if (!settings) return <AdminShell><div className="p-6 text-sm">Loading…</div></AdminShell>;
  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4 max-w-5xl" data-testid="emergency-dispatch-page">
        <div className="flex items-start gap-3 flex-wrap">
          <Siren weight="fill" size={28} className="text-red-600"/>
          <div className="flex-1">
            <h1 className="text-2xl font-black uppercase tracking-tight">Emergency Dispatch</h1>
            <p className="text-xs text-slate-600">Replaces AppFolio Smart Maintenance: after-hours call → AI triage → auto WO → rings the on-call chain until someone presses 1. Skeleton + test panel; turn on per company when their number is approved.</p>
          </div>
          <label className="flex items-center gap-2 border-2 border-slate-900 px-3 py-2 bg-white cursor-pointer" data-testid="dispatch-toggle">
            <input type="checkbox" checked={settings.enabled} onChange={e => save({ enabled: e.target.checked })} className="w-4 h-4 accent-red-600"/>
            <span className="text-xs font-black uppercase tracking-widest">{settings.enabled ? "Live" : "Off"}</span>
          </label>
        </div>
        {settings.enabled && !settings.phone_number && (
          <div className="border-2 border-amber-500 bg-amber-50 p-3 text-xs flex items-center gap-2"><Warning weight="fill" className="text-amber-700"/> Live but no number set — enter the Twilio number below once approved.</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="border-2 border-slate-900 bg-white p-4 space-y-2">
              <div className="font-black uppercase tracking-widest text-xs">Line settings</div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500">Twilio number (E.164)
                <input value={settings.phone_number} onChange={e => setSettings({ ...settings, phone_number: e.target.value })} onBlur={() => save({})} placeholder="+18883717701" className="mt-1 w-full h-9 px-2 border border-slate-300 text-sm font-mono" data-testid="dispatch-phone"/></label>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500">Ring each level for (sec)
                <input type="number" value={settings.ring_timeout_sec} onChange={e => setSettings({ ...settings, ring_timeout_sec: +e.target.value })} onBlur={() => save({})} className="mt-1 w-32 h-9 px-2 border border-slate-300 text-sm"/></label>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500">Greeting
                <textarea value={settings.greeting} onChange={e => setSettings({ ...settings, greeting: e.target.value })} onBlur={() => save({})} rows={3} className="mt-1 w-full px-2 py-1 border border-slate-300 text-xs"/></label>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500">Text to caller when accepted (use {"{tech}"} {"{eta}"})
                <textarea value={settings.accepted_sms} onChange={e => setSettings({ ...settings, accepted_sms: e.target.value })} onBlur={() => save({})} rows={2} className="mt-1 w-full px-2 py-1 border border-slate-300 text-xs"/></label>
              <div className="text-[10px] text-slate-500">On-call chain comes from <b>Emergency Protocol → On-call shifts</b> (L1 → L2 → L3).</div>
            </div>
            <Checklist data={checklist}/>
          </div>

          <div className="space-y-4">
            <div className="border-2 border-red-600 bg-red-50 p-4 space-y-2" data-testid="simulate-panel">
              <div className="font-black uppercase tracking-widest text-xs text-red-900 flex items-center gap-2"><PlayCircle weight="fill"/> Test panel · simulate an after-hours call</div>
              <div className="flex flex-wrap gap-1">{SAMPLES.map((s, i) => <button key={i} onClick={() => setTranscript(s)} className="text-[10px] px-2 py-1 border border-red-300 bg-white hover:bg-red-100" data-testid={`sample-${i}`}>{["Flood", "Lockout", "Drip (non-emergency)", "Gas"][i]}</button>)}</div>
              <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={4} className="w-full px-2 py-1 border border-red-300 text-xs bg-white" data-testid="simulate-transcript"/>
              <button onClick={simulate} disabled={busy} className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-60" data-testid="simulate-run">{busy ? "Triaging…" : "Run call through dispatch"}</button>
            </div>
            <div className="space-y-2">
              <div className="font-black uppercase tracking-widest text-xs">Incidents ({incidents.length})</div>
              {incidents.length === 0 && <div className="text-xs text-slate-500">No incidents yet — run a simulated call.</div>}
              {incidents.map(i => <Incident key={i.id} inc={i} onChange={load}/>)}
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
